import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ShieldCheck, ShieldAlert, User, Wallet, CreditCard, FileDown } from "lucide-react";
import type { AuditLog } from "@shared/schema";

interface AuditLogTableProps {
  logs: AuditLog[];
}

const eventTypeConfig = {
  authorization: { icon: ShieldCheck, label: "Authorization", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800" },
  wallet: { icon: Wallet, label: "Wallet", className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800" },
  payment: { icon: CreditCard, label: "Payment", className: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800" },
  data_export: { icon: FileDown, label: "Data Export", className: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800" },
  system: { icon: ShieldCheck, label: "System", className: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800" },
  compliance: { icon: ShieldAlert, label: "Compliance", className: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800" },
  access: { icon: User, label: "Access", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800" },
};

const severityConfig = {
  info: { label: "Info", className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800" },
  warning: { label: "Warning", className: "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800" },
  critical: { label: "Critical", className: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800" },
};

export function AuditLogTable({ logs }: AuditLogTableProps) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">Timestamp</TableHead>
            <TableHead className="font-semibold">Event Type</TableHead>
            <TableHead className="font-semibold">Severity</TableHead>
            <TableHead className="font-semibold">Actor</TableHead>
            <TableHead className="font-semibold">Action</TableHead>
            <TableHead className="font-semibold">Resource</TableHead>
            <TableHead className="font-semibold">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No audit logs found
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => {
              const eventConfig = eventTypeConfig[log.eventType as keyof typeof eventTypeConfig];
              const EventIcon = eventConfig?.icon || ShieldCheck;
              const severityConf = severityConfig[log.severity as keyof typeof severityConfig];
              const timestamp = log.timestamp ? new Date(log.timestamp) : new Date();
              
              return (
                <TableRow key={log.id} className="hover-elevate" data-testid={`row-audit-${log.id}`}>
                  <TableCell className="font-mono text-sm">
                    <div>{format(timestamp, "MMM d, yyyy")}</div>
                    <div className="text-xs text-muted-foreground">{format(timestamp, "h:mm:ss a")}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={eventConfig?.className || ""}>
                      <EventIcon className="h-3 w-3 mr-1" />
                      {eventConfig?.label || log.eventType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={severityConf?.className || ""}>
                      {severityConf?.label || log.severity}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{log.actor}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{log.resourceType}</div>
                      <div className="font-mono text-xs text-muted-foreground">{log.resourceId}</div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    {log.details && (
                      <div className="text-sm text-muted-foreground truncate">{log.details}</div>
                    )}
                    {log.ipAddress && (
                      <div className="font-mono text-xs text-muted-foreground">{log.ipAddress}</div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
